import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, Button, Select, Typography, Row, Col } from 'antd';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import createPatient from '../../utils/patient/createPatient';
import updatePatient from '../../utils/patient/updatePatient';
import { useTranslation } from 'react-i18next';
import LinkButton from '../buttons/LinkButton';
import { DownOutlined } from '@ant-design/icons';
import { getCurrentUser } from '../../utils/userStorage';
import { getPhonePlaceholder } from '../call/CallDialer';

const { Text } = Typography;

export default function PatientForm({
                                      isOpen,
                                      onCancel,
                                      onSave,
                                      editingPatient = null,
                                      buttonStyle = {},
                                    }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [showMoreFields, setShowMoreFields] = useState(false);
  const currentUser = getCurrentUser();

  const mapFormValuesToAttributes = (values) => ({
    full_name: values.name,
    gender: values.gender,
    email: values.email,
    mobile_number: values.mobile,
    external_id: values.patientId,
    location: values.location,
    dob: values.dob || null,
    age: values.age,
    link: values.link,
    comments: values.comments,
    civil_status: values.civil_status,
    occupation: values.occupation,
    religion: values.religion,
    weight: values.weight,
    height: values.height,
    blood_type: values.blood_type,
    bmi: values.bmi
  });

  useEffect(() => {
    if (isOpen && editingPatient) {
      form.setFieldsValue({
        name: editingPatient.getFullName(),
        gender: editingPatient.getGenderValue(),
        email: editingPatient.getEmail(),
        mobile: editingPatient.getMobileNumber(),
        patientId: editingPatient.getExternalId(),
        location: editingPatient.getLocation(),
        link: editingPatient.getLink(),
        dob: editingPatient.dob || '',
        age: editingPatient.age || '',
        comments: editingPatient.getComments(),
        civil_status: editingPatient.getCivilStatus(),
        occupation: editingPatient.getOccupation(),
        religion: editingPatient.getReligion(),
        weight: editingPatient.getWeight(),
        height: editingPatient.getHeight(),
        blood_type: editingPatient.getBloodType(),
        bmi: editingPatient.getBMI()
      });
      setShowMoreFields(true);
    } else if (isOpen) {
      form.resetFields();
      setShowMoreFields(false);
    }
  }, [isOpen, editingPatient, form]);

  const handleSubmit = async (values) => {
    const attributes = mapFormValuesToAttributes(values);

    try {
      if (editingPatient) {
        const updatedPatient = await updatePatient(editingPatient.getUUID(), attributes);
        toast.success(t('common.saved'));
        onSave(updatedPatient, true);
      } else {
        const createdPatient = await createPatient(attributes);
        toast.success(t('common.saved'));
        onSave(createdPatient, false);
      }
    } catch (error) {
      toast.error(t('patient_form.errors.save_failed'));
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const toggleMoreFields = () => {
    setShowMoreFields(!showMoreFields);
  };

  // Calculate age from date of birth using moment.js
  const calculateAge = (dob) => {
    if (!dob) return '';
    
    const birthDate = moment(dob);
    const today = moment();
    
    // Check if the date is valid
    if (!birthDate.isValid()) return '';
    
    // Validate year range (between 1900 and current year)
    const currentYear = today.year();
    const birthYear = birthDate.year();
    
    if (birthYear < 1900 || birthYear > currentYear) {
      return '';
    }
    
    // Calculate age using moment's diff method
    const age = today.diff(birthDate, 'years');
    
    return age > 0 ? age.toString() : '';
  };

  // Handle DOB change and automatically calculate age
  const handleDOBChange = (e) => {
    const dob = e.target.value;
    const calculatedAge = calculateAge(dob);
    
    // Update the age field with the calculated value
    form.setFieldsValue({ age: calculatedAge });
  };

  const renderBasicInfoFields = () => (
    <>
      <Col span={24}>
        <Form.Item
          name="name"
          label={<Text type="secondary">{t('patient_form.fields.full_name')}</Text>}
          rules={[{ required: true, message: t('patient_form.errors.full_name_missing') }]}
        >
          <Input style={{ fontSize: '16px' }}/>
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item
          name="gender"
          label={<Text type="secondary">{t('patient_form.fields.gender')}</Text>}
        >
          <Select style={{ fontSize: '16px' }} >
            <Select.Option value="male" style={{ fontSize: '16px' }}>{t('patient_form.fields.gender_male')}</Select.Option>
            <Select.Option value="female" style={{ fontSize: '16px' }}>{t('patient_form.fields.gender_female')}</Select.Option>
            <Select.Option value="non_binary" style={{ fontSize: '16px' }}>{t('patient_form.fields.gender_non_binary')}</Select.Option>
          </Select>
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item
          name="dob"
          label={<Text type="secondary">{t('patient_form.fields.dob')}</Text>}
        >
          <Input 
            type="date" 
            style={{ fontSize: '16px' }} 
            onChange={handleDOBChange}
          />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item
          name="age"
          label={<Text type="secondary">{t('patient_form.fields.age')}</Text>}
        >
          <Input style={{ fontSize: '16px' }} />
        </Form.Item>
      </Col>
    </>
  );

  const renderContactFields = () => (
    <>
      <Col span={12}>
        <Form.Item
          name="mobile"
          label={<Text type="secondary">{t('patient_form.fields.mobile_number')}</Text>}
          rules={[
            {
              pattern: /^\+?[0-9\s\-()]{7,20}$/,
              message: t('patient_form.errors.invalid_mobile_number'),
            },
          ]}
        >
          <Input 
            type="tel" 
            style={{ fontSize: '16px' }}
            placeholder={getPhonePlaceholder()}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="email"
          label={<Text type="secondary">{t('patient_form.fields.email')}</Text>}
          rules={[{ type: 'email', message: t('patient_form.errors.invalid_email') }]}
        >
          <Input 
            type="email" 
            style={{ fontSize: '16px' }}
            placeholder="patient@gmail.com"
          />
        </Form.Item>
      </Col>
    </>
  );

  const renderAdditionalFields = () => (
          <>
      <Col span={12}>
            <Form.Item
              name="patientId"
              label={<Text type="secondary">{t('patient_form.fields.id')}</Text>}
            >
              <Input style={{ fontSize: '16px' }}/>
            </Form.Item>
      </Col>
      <Col span={12}>
            <Form.Item
              name="location"
              label={<Text type="secondary">{t('patient_form.fields.location_prefix')}</Text>}
            >
              <Input style={{ fontSize: '16px' }}/>
            </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="civil_status"
          label={<Text type="secondary">{t('patient_form.fields.civil_status')}</Text>}
        >
          <Input style={{ fontSize: '16px' }}/>
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="occupation"
          label={<Text type="secondary">{t('patient_form.fields.occupation')}</Text>}
        >
          <Input style={{ fontSize: '16px' }}/>
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="religion"
          label={<Text type="secondary">{t('patient_form.fields.religion')}</Text>}
        >
          <Input style={{ fontSize: '16px' }}/>
        </Form.Item>
      </Col>
      <Col span={12}>
            <Form.Item
              name="link"
              label={<Text type="secondary">{t('patient_form.fields.link')}</Text>}
            >
              <Input placeholder="https://myhospital.com/patient/12345" type="url" style={{ fontSize: '16px' }}/>
            </Form.Item>
      </Col>
    </>
  );

  const renderMedicalFields = () => (
    <>
      <Col span={12}>
        <Form.Item
          name="weight"
          label={<Text type="secondary">{t('patient_form.fields.weight')}</Text>}
        >
          <Input style={{ fontSize: '16px' }}/>
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="height"
          label={<Text type="secondary">{t('patient_form.fields.height')}</Text>}
        >
          <Input style={{ fontSize: '16px' }}/>
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="blood_type"
          label={<Text type="secondary">{t('patient_form.fields.blood_type')}</Text>}
        >
          <Input style={{ fontSize: '16px' }}/>
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="bmi"
          label={<Text type="secondary">{t('patient_form.fields.bmi')}</Text>}
        >
          <Input style={{ fontSize: '16px' }}/>
        </Form.Item>
      </Col>
    </>
  );

  return (
    <Modal
      forceRender
      title={editingPatient ? t('patient_form.edit_title') : t('patient_form.new_title')}
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={800}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        validateTrigger="onBlur"
      >
        <Row gutter={16}>
          {renderBasicInfoFields()}
          {renderContactFields()}
          
          {!editingPatient && !showMoreFields && (
            <Col span={24}>
              <LinkButton 
                onClick={toggleMoreFields} 
                style={{ margin: '8px 0 16px', display: 'flex', alignItems: 'center', gap: 4 }}
              >
                {t('patient_form.show_more_fields')} <DownOutlined />
              </LinkButton>
            </Col>
          )}

          {(editingPatient || showMoreFields) && (
            <>
              {renderAdditionalFields()}
              {renderMedicalFields()}
              <Col span={24}>
            <Form.Item
              name="comments"
              label={<Text type="secondary">{t('patient_form.fields.comments')}</Text>}
            >
              <Input.TextArea style={{ fontSize: '16px' }}/>
            </Form.Item>
              </Col>
          </>
        )}

          <Col span={24}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          <Button onClick={handleCancel}>{t('patient_form.cancel')}</Button>
          <Button type="primary" htmlType="submit" style={buttonStyle.primary}>
            {editingPatient ? t('patient_form.update') : t('patient_form.create')}
          </Button>
        </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}